import { KrCard } from '@/common/card/KrCard';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { McoDispBreakdownPieChart } from '@/sc/entities/mcoInv/dashboard/fin/McoDispBreakdownPieChart';
import { McoDispTrendChart } from '@/sc/entities/mcoInv/dashboard/fin/McoDispTrendChart';
import { McoFinTopCtrtCard } from '@/sc/entities/mcoInv/dashboard/fin/McoFinTopCtrtCard';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Checkbox, ListItemText, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useState } from 'react';
import { McoFinTrendChart } from './McoFinTrendChart';

export const McoFinDashboard = () => {
  const rsrc = useRsrc();
  const { navState, assign } = useNavState();
  const dashboardNavState = navState['mcoInvDashboardState'] ?? {};

  const [isUnits, setIsUnits] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [pbms, setPbms] = useState();
  const [selectedPbms, setSelectedPbms] = useState([]);

  const [ctrts, setCtrts] = useState();
  const [selectedCtrts, setSelectedCtrts] = useState([]);

  // const [progTypes, setProgTypes] = useState();
  // const [selectedProgTypes, setSelectedProgTypes] = useState([]);

  const [paidIsChart, setPaidIsChart] = useState(true);
  const [disputeIsChart, setDisputeIsChart] = useState(true);

  useEffect(() => {
    rsrc.data('sc', 'MCO_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      setPeriods(res.periods);
      // Restore nav state
      if (dashboardNavState.selectedPeriodId) {
        const period = res.periods.find((period) => period.id === dashboardNavState.selectedPeriodId);
        if (period) setSelectedPeriod(period);
      }
      // or set to first period
      else if (res.periods?.length > 0) {
        const selectedPeriod = res.periods[0];
        setSelectedPeriod(selectedPeriod);
      }

      setPbms(res.pbms);
      if (dashboardNavState.selectedPbmCodes) {
        const matchedPbms = res.pbms.filter((pbm) => dashboardNavState.selectedPbmCodes.includes(pbm.code));
        if (matchedPbms.length > 0) setSelectedPbms(matchedPbms);
      } else {
        setSelectedPbms(res.pbms);
      }

      setCtrts(res.ctrts);
      // Restore nav state
      if (dashboardNavState.selectedCtrtCodes) {
        const matchedCtrts = res.ctrts.filter((ctrt) => dashboardNavState.selectedCtrtCodes.includes(ctrt.code));
        if (matchedCtrts.length > 0) setSelectedCtrts(matchedCtrts);
      } else {
        setSelectedCtrts(res.ctrts);
      }

      // setProgTypes(res.progTypes);
      // // Restore nav state
      // if (dashboardNavState.selectedProgTypes) {
      //   const matchedProgTypes = res.progTypes.filter(progType =>
      //     dashboardNavState.selectedProgTypes.includes(progType)
      //   );
      //   if (matchedProgTypes.length > 0) setSelectedProgTypes(matchedProgTypes);
      // } else {
      //   setSelectedProgTypes(res.progTypes);
      // }
    });
  }, []);

  const saveNavState = () => {
    const newDashboardNavState = {
      selectedPeriodId: selectedPeriod?.id,
      selectedPbmCodes: selectedPbms?.map((pbm) => pbm.code),
      selectedCtrtCodes: selectedCtrts?.map((ctrt) => ctrt.code),
    };

    assign('mcoInvDashboardState', newDashboardNavState);
  };

  useEffect(() => {
    saveNavState();
  }, [selectedPeriod, selectedPbms, selectedCtrts]);

  if (!periods || !pbms || !ctrts) return null;

  const paidTrendButtons = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={paidIsChart}
      sx={{ width: 'fit-content' }}
      onChange={(event, value) => {
        if (value !== null) setPaidIsChart(value);
      }}
    >
      <ToggleButton value={true} aria-label="chart">
        Chart
      </ToggleButton>
      <ToggleButton value={false} aria-label="table">
        Table
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const disputeTrendButtons = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={disputeIsChart}
      sx={{ width: 'fit-content' }}
      onChange={(event, value) => {
        if (value !== null) setDisputeIsChart(value);
      }}
    >
      <ToggleButton value={true} aria-label="chart">
        Chart
      </ToggleButton>
      <ToggleButton value={false} aria-label="table">
        Table
      </ToggleButton>
    </ToggleButtonGroup>
  );

  if (!periods || !pbms || !ctrts) return null;

  return (
    <ContentBox>
      {/* Title */}
      <Title
        icon={fas.faTasks}
        title="Managed Care Financial Dashboard"
        buttons={
          <ToggleButtonGroup
            exclusive
            value={isUnits}
            sx={{ height: 'fit-content' }}
            onChange={(event, value) => {
              if (value !== null) setIsUnits(value);
            }}
          >
            <ToggleButton value={false} aria-label="dollars">
              Dollars
            </ToggleButton>
            <ToggleButton value={true} aria-label="units">
              Units
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        {/* Period */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Invoice Period"
          value={selectedPeriod?.id || ''}
          onChange={(event) => {
            const period = periods.find((period) => period.id === event.target.value);
            setSelectedPeriod(period);
          }}
        >
          {periods?.map((period) => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>

        {/* PBMs */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="PBMs"
          value={selectedPbms}
          onChange={(event) => setSelectedPbms(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.name).join(', '),
          }}
        >
          {pbms?.map((pbm) => (
            <MenuItem key={pbm.code} value={pbm}>
              <Checkbox checked={selectedPbms?.some((p) => p.code === pbm.code)} />
              <ListItemText primary={pbm.name} />
            </MenuItem>
          ))}
        </TextField>

        {/* Contracts */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Contracts"
          value={selectedCtrts}
          onChange={(event) => setSelectedCtrts(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.name).join(', '),
          }}
        >
          {ctrts?.map((ctrt) => (
            <MenuItem key={ctrt.code} value={ctrt}>
              <Checkbox checked={selectedCtrts?.some((p) => p.code === ctrt.code)} />
              <ListItemText primary={ctrt.name} />
            </MenuItem>
          ))}
        </TextField>

        {/* Prog Types */}
        {/* <TextField fullWidth select
          variant="outlined"
          label="Program Types"
          value={selectedProgTypes}
          onChange={event => setSelectedProgTypes(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p).join(', ')
          }}>
          {progTypes?.map(progType => (
            <MenuItem key={progType} value={progType}>
              <Checkbox checked={selectedProgTypes?.some((p) => p === progType)} />
              <ListItemText primary={progType} />
            </MenuItem>
          ))}
        </TextField> */}
      </Box>

      {/* Main Contents */}
      <SpacerLayout>
        <Spacer vertical>
          {/* Row 1 */}
          <Spacer weights={[1, 1]}>
            <KrCard title="Paid Trends" sx={{ flex: 1 }} buttons={paidTrendButtons}>
              <McoFinTrendChart
                selectedPeriod={selectedPeriod}
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                isUnits={isUnits}
                isPaid={true}
                isChart={paidIsChart}
              />
            </KrCard>

            <KrCard title="Dispute Trends" sx={{ flex: 1 }} buttons={disputeTrendButtons}>
              <McoFinTrendChart
                selectedPeriod={selectedPeriod}
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                isUnits={isUnits}
                isPaid={false}
                isChart={disputeIsChart}
              />
            </KrCard>
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[3, 2, 2]}>
            <McoFinTopCtrtCard
              selectedPeriod={selectedPeriod}
              selectedPbms={selectedPbms}
              selectedCtrts={selectedCtrts}
            />

            <KrCard title="Dispute Code Breakdown" sx={{ flex: 1 }}>
              <McoDispBreakdownPieChart
                selectedPeriod={selectedPeriod}
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
              />
            </KrCard>

            <KrCard title="Dispute Code Trends" sx={{ flex: 1 }}>
              <McoDispTrendChart
                selectedPeriod={selectedPeriod}
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                // selectedProgTypes={selectedProgTypes}
                isUnits={isUnits}
              />
            </KrCard>
          </Spacer>
        </Spacer>
      </SpacerLayout>
    </ContentBox>
  );
};
